import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  useToast,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  IconButton,
  Card,
  CardBody,
  FormErrorMessage,
  Text,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsPlus, BsTrash } from "react-icons/bs";
import procurementApi from "../services/procurementApi";
import { employeeService } from "../../employee/employeeService";

const WorkflowCreate = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [stages, setStages] = useState([
    {
      id: Date.now(),
      stageName: "",
      approverType: "ROLE",
      approverRole: "",
      approverUsers: [],
      order: 1,
      isRequired: true,
    },
  ]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const data = await employeeService.getAllEmployees();
      setEmployees(data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const addStage = () => {
    setStages([
      ...stages,
      {
        id: Date.now(),
        stageName: "",
        approverType: "ROLE",
        approverRole: "",
        approverUsers: [],
        order: stages.length + 1,
        isRequired: true,
      },
    ]);
  };

  const updateStage = (id, field, value) => {
    setStages(
      stages.map((stage) =>
        stage.id === id ? { ...stage, [field]: value } : stage,
      ),
    );
  };

  const removeStage = (id) => {
    setStages(stages.filter((stage) => stage.id !== id));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = "Workflow name must be at least 3 characters";
    }

    if (stages.length === 0) {
      newErrors.stages = "At least one stage is required";
      toast({
        title: "Validation Error",
        description: "Please add at least one approval stage",
        status: "error",
        duration: 3000,
      });
    }

    const invalidStages = stages.filter(
      (stage) =>
        !stage.stageName ||
        (stage.approverType === "ROLE" && !stage.approverRole) ||
        (stage.approverType === "USERS" &&
          (!stage.approverUsers || stage.approverUsers.length === 0)),
    );
    if (invalidStages.length > 0) {
      newErrors.stages = "All stages must have name and approver";
      toast({
        title: "Validation Error",
        description: "Please complete all stage fields (name and approver)",
        status: "error",
        duration: 3000,
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const workflowData = {
        ...formData,
        stages: stages.map(({ id, ...stage }) => stage),
      };
      await procurementApi.createWorkflow(workflowData);
      toast({
        title: "Workflow created successfully",
        status: "success",
        duration: 3000,
      });
      navigate("/procurement/workflows");
    } catch (error) {
      toast({
        title: "Error creating workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/procurement/workflows");
  };

  return (
    <Box p={6}>
      <HStack mb={6}>
        <Button
          leftIcon={<BsArrowLeft />}
          variant="ghost"
          onClick={handleCancel}
        >
          Back
        </Button>
        <Heading size="lg">Create Workflow</Heading>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" borderWidth="1px">
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Workflow Details */}
            <FormControl isInvalid={errors.name} isRequired>
              <FormLabel>Workflow Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Standard Procurement Approval"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Describe this workflow"
                rows={3}
              />
            </FormControl>

            {/* Approval Stages */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="sm">Approval Stages</Heading>
                <Button
                  leftIcon={<BsPlus />}
                  size="sm"
                  onClick={addStage}
                  variant="outline"
                  colorScheme="blue"
                >
                  Add Stage
                </Button>
              </HStack>

              <VStack spacing={4} align="stretch">
                {stages.map((stage, index) => (
                  <Card key={stage.id}>
                    <CardBody>
                      <VStack align="stretch" spacing={4}>
                        <HStack align="start" spacing={4}>
                          <FormControl flex={1} isRequired>
                            <FormLabel fontSize="sm">Stage Name</FormLabel>
                            <Input
                              size="sm"
                              value={stage.stageName}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "stageName",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g., Manager Approval"
                            />
                          </FormControl>

                          <FormControl maxW="150px">
                            <FormLabel fontSize="sm">Approver Type</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverType || "ROLE"}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "approverType",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="ROLE">By ROLE</option>
                              <option value="USERS">Specific USERS</option>
                            </Select>
                          </FormControl>

                          <FormControl maxW="100px">
                            <FormLabel fontSize="sm">Order</FormLabel>
                            <Input
                              size="sm"
                              type="number"
                              value={stage.order}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "order",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                            />
                          </FormControl>

                          <IconButton
                            mt={7}
                            size="sm"
                            icon={<BsTrash />}
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeStage(stage.id)}
                            aria-label="Remove stage"
                            isDisabled={stages.length === 1}
                          />
                        </HStack>

                        {/* Approver ROLE Selection */}
                        {(stage.approverType === "ROLE" ||
                          !stage.approverType) && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Approver ROLE</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverRole}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "approverRole",
                                  e.target.value,
                                )
                              }
                              placeholder="Select ROLE"
                            >
                              <option value="SuperAdmin">Super Admin</option>
                              <option value="Admin">Admin</option>
                              <option value="HR">HR</option>
                              <option value="Manager">Manager</option>
                              <option value="Employee">Employee</option>
                            </Select>
                          </FormControl>
                        )}

                        {/* Approver USERS Selection */}
                        {stage.approverType === "USERS" && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">
                              Select Approvers
                              {stage.approverUsers?.length > 0 && (
                                <Badge ml={2} colorScheme="blue">
                                  {stage.approverUsers.length} selected
                                </Badge>
                              )}
                            </FormLabel>
                            {employees.length === 0 ? (
                              <Text fontSize="sm" color="gray.500">
                                No employees found. Please add employees first.
                              </Text>
                            ) : (
                              <Box
                                maxH="150px"
                                overflowY="auto"
                                p={2}
                                borderWidth="1px"
                                borderRadius="md"
                              >
                                <CheckboxGroup
                                  value={stage.approverUsers || []}
                                  onChange={(values) =>
                                    updateStage(
                                      stage.id,
                                      "approverUsers",
                                      values,
                                    )
                                  }
                                >
                                  <Wrap spacing={2}>
                                    {employees.map((emp) => (
                                      <WrapItem key={emp._id}>
                                        <Checkbox value={emp._id} size="sm">
                                          {emp.firstName} {emp.lastName}
                                          {emp.ROLE && (
                                            <Text
                                              as="span"
                                              fontSize="xs"
                                              color="gray.500"
                                              ml={1}
                                            >
                                              ({emp.ROLE})
                                            </Text>
                                          )}
                                        </Checkbox>
                                      </WrapItem>
                                    ))}
                                  </Wrap>
                                </CheckboxGroup>
                              </Box>
                            )}
                          </FormControl>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            </Box>

            {/* Action Buttons */}
            <HStack spacing={4} justify="flex-end" pt={4}>
              <Button
                variant="outline"
                onClick={handleCancel}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" colorScheme="blue" isLoading={loading}>
                Create Workflow
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default WorkflowCreate;
